// Zeitraum-Auswahl der Admin-Protokolle (API-Protokoll, Sendeverlauf).
//
// PostgREST liefert je Anfrage hoechstens 1000 Zeilen und sagt es nicht.
// Bis Oktober 2026 rechnete das API-Protokoll seine Summen deshalb ueber die
// neuesten 1000 von 11566 Aufrufen, der Sendeverlauf sah 1000 von 6644
// Berichten. Die Dienste laden inzwischen seitenweise alles; damit „alles“
// nicht bei jedem Oeffnen tausende Zeilen bedeutet, filtern beide Ansichten
// den Zeitraum in der DATENBANK. Diese Datei uebersetzt die Auswahl im UI in
// die Grenzen der Abfrage: `ab` einschliesslich, `bis` ausschliesslich, beide
// als ISO-Zeitpunkt (UTC).
//
// Ortszeit: die Nutzer sitzen in Europe/Berlin. „Dieser Monat“ beginnt um
// Mitternacht ORTSZEIT am Ersten — `new Date(j, m, t)` rechnet lokal,
// `toISOString()` macht daraus UTC (1.10. 00:00 MESZ = 30.09. 22:00Z).
// Ueber Monats- und Jahresgrenzen rollt `new Date` selbst (Tag 0, Tag 32).
//
// Reine Funktionen, `jetzt` wird hineingereicht: testbar ohne Uhr und DOM.

export type ZeitraumVoreinstellung =
    | 'dieserMonat'
    | 'letzte30Tage'
    | 'letzte90Tage'
    | 'letzte12Monate'
    | 'alles'
    | 'benutzerdefiniert';

/** Grenzen der Abfrage: `ab` einschliesslich, `bis` ausschliesslich (ISO). Fehlt eine, ist die Seite offen. */
export interface ZeitraumGrenzen {
    ab?: string;
    bis?: string;
}

/** Von/Bis-Felder des UI als 'YYYY-MM-DD', leer = offen. `end` zaehlt EINSCHLIESSLICH. */
export interface BenutzerZeitraum {
    start: string;
    end: string;
}

export interface ZeitraumOption<V extends ZeitraumVoreinstellung = ZeitraumVoreinstellung> {
    readonly wert: V;
    readonly label: string;
}

/** API-Protokoll: Kosten laufen monatlich auf, deshalb „Dieser Monat“ als Standard. */
export const API_LOG_VOREINSTELLUNGEN = [
    { wert: 'dieserMonat', label: 'Dieser Monat' },
    { wert: 'letzte30Tage', label: 'Letzte 30 Tage' },
    { wert: 'letzte12Monate', label: 'Letzte 12 Monate' },
    { wert: 'alles', label: 'Alles' },
] as const satisfies readonly ZeitraumOption[];

export type ApiLogVoreinstellung = (typeof API_LOG_VOREINSTELLUNGEN)[number]['wert'];
export const API_LOG_STANDARD: ApiLogVoreinstellung = 'dieserMonat';

/** Admin-Sendeverlauf: Standard die letzten 90 Tage, dazu freie Von/Bis-Felder. */
export const SENDE_VOREINSTELLUNGEN = [
    { wert: 'letzte90Tage', label: 'Letzte 90 Tage' },
    { wert: 'letzte12Monate', label: 'Letzte 12 Monate' },
    { wert: 'alles', label: 'Alles' },
    { wert: 'benutzerdefiniert', label: 'Benutzerdefiniert' },
] as const satisfies readonly ZeitraumOption[];

export type SendeVoreinstellung = (typeof SENDE_VOREINSTELLUNGEN)[number]['wert'];
export const SENDE_STANDARD: SendeVoreinstellung = 'letzte90Tage';

/**
 * Gemerkter Wert (sessionStorage) zurueck in eine gueltige Voreinstellung.
 * Ein alter oder fremder Wert faellt auf den Standard, statt die Abfrage mit
 * einem unbekannten Zeitraum zu fahren.
 */
export const gueltigeVoreinstellung = <V extends ZeitraumVoreinstellung>(
    wert: unknown,
    optionen: readonly ZeitraumOption<V>[],
    standard: V,
): V => optionen.find((o) => o.wert === wert)?.wert ?? standard;

/**
 * 'YYYY-MM-DD' als lokale Mitternacht. `null` fuer leer, falsches Format oder
 * einen Tag, den es nicht gibt (2026-02-30 rollte sonst still in den Maerz,
 * Jahr 0002 wuerde zu 1902).
 */
export const parseTag = (tag: string | null | undefined): Date | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((tag ?? '').trim());
    if (!m) return null;
    const jahr = Number(m[1]);
    const monat = Number(m[2]) - 1;
    const tagImMonat = Number(m[3]);
    const datum = new Date(jahr, monat, tagImMonat);
    if (datum.getFullYear() !== jahr || datum.getMonth() !== monat || datum.getDate() !== tagImMonat) return null;
    return datum;
};

/** Lokale Mitternacht `tage` Tage nach (negativ: vor) dem Kalendertag von `datum`. */
const tagesbeginn = (datum: Date, tage = 0): Date =>
    new Date(datum.getFullYear(), datum.getMonth(), datum.getDate() + tage);

const zweistellig = (n: number): string => String(n).padStart(2, '0');

/** Lokaler Kalendertag als 'YYYY-MM-DD' (Wert eines `<input type="date">`). */
export const alsTagIso = (datum: Date): string =>
    `${datum.getFullYear()}-${zweistellig(datum.getMonth() + 1)}-${zweistellig(datum.getDate())}`;

/** Lokaler Kalendertag als 'TT.MM.JJJJ'. */
export const alsTagText = (datum: Date): string =>
    `${zweistellig(datum.getDate())}.${zweistellig(datum.getMonth() + 1)}.${datum.getFullYear()}`;

/**
 * Voreinstellung + `jetzt` → Grenzen der Abfrage.
 *
 * - „Dieser Monat“: ab dem Ersten des laufenden Monats, 00:00 Ortszeit.
 * - „Letzte 30/90 Tage“: ab Mitternacht des Kalendertags vor 30/90 Tagen;
 *   heute zaehlt mit (23.10. → ab 23.09.).
 * - „Letzte 12 Monate“: ab demselben Kalendertag des Vorjahres (23.10.2026 →
 *   ab 23.10.2025; 29.02. → 01.03. des Vorjahres).
 * - „Alles“: keine Grenze.
 * - „Benutzerdefiniert“: Von ab Mitternacht, Bis EINSCHLIESSLICH — an die
 *   Datenbank geht deshalb Mitternacht des Folgetags als `bis`. Leere oder
 *   ungueltige Felder lassen ihre Seite offen.
 */
export const zeitraumGrenzen = (
    voreinstellung: ZeitraumVoreinstellung,
    jetzt: Date,
    benutzer?: BenutzerZeitraum,
): ZeitraumGrenzen => {
    switch (voreinstellung) {
        case 'dieserMonat':
            return { ab: new Date(jetzt.getFullYear(), jetzt.getMonth(), 1).toISOString() };
        case 'letzte30Tage':
            return { ab: tagesbeginn(jetzt, -30).toISOString() };
        case 'letzte90Tage':
            return { ab: tagesbeginn(jetzt, -90).toISOString() };
        case 'letzte12Monate':
            return { ab: new Date(jetzt.getFullYear() - 1, jetzt.getMonth(), jetzt.getDate()).toISOString() };
        case 'alles':
            return {};
        case 'benutzerdefiniert': {
            const grenzen: ZeitraumGrenzen = {};
            const von = parseTag(benutzer?.start);
            const bisTag = parseTag(benutzer?.end);
            if (von) grenzen.ab = von.toISOString();
            if (bisTag) grenzen.bis = tagesbeginn(bisTag, 1).toISOString();
            return grenzen;
        }
    }
};

/**
 * Was eine Voreinstellung als Von/Bis-Felder bedeutet — zum Vorbelegen, wenn
 * jemand von einer Voreinstellung auf „Benutzerdefiniert“ wechselt, statt mit
 * leeren Feldern (= alles) zu starten. Bis ist heute (einschliesslich).
 */
export const alsBenutzerZeitraum = (voreinstellung: ZeitraumVoreinstellung, jetzt: Date): BenutzerZeitraum => {
    if (voreinstellung === 'alles' || voreinstellung === 'benutzerdefiniert') return { start: '', end: '' };
    const { ab } = zeitraumGrenzen(voreinstellung, jetzt);
    return { start: ab ? alsTagIso(new Date(ab)) : '', end: alsTagIso(jetzt) };
};

/**
 * Grenzen fuer Menschen, Bis wieder einschliesslich: „ab 01.10.2026“,
 * „bis 15.10.2026“, „01.10.2026 – 15.10.2026“, „gesamter Zeitraum“.
 */
export const zeitraumText = (grenzen: ZeitraumGrenzen): string => {
    const ab = grenzen.ab ? new Date(grenzen.ab) : null;
    const bisAusschliesslich = grenzen.bis ? new Date(grenzen.bis) : null;
    const bis = bisAusschliesslich ? tagesbeginn(bisAusschliesslich, -1) : null;
    if (ab && bis) {
        const von = alsTagText(ab);
        const nach = alsTagText(bis);
        return von === nach ? von : `${von} – ${nach}`;
    }
    if (ab) return `ab ${alsTagText(ab)}`;
    if (bis) return `bis ${alsTagText(bis)}`;
    return 'gesamter Zeitraum';
};

/**
 * Meldung eines Ladefehlers fuer die Ansicht. Supabase wirft keine `Error`,
 * sondern ein Objekt mit `message` (PostgrestError).
 */
export const ladefehlerText = (e: unknown): string => {
    if (e instanceof Error && e.message) return e.message;
    if (e && typeof e === 'object' && 'message' in e) {
        const { message } = e as { message: unknown };
        if (typeof message === 'string' && message) return message;
    }
    if (typeof e === 'string' && e) return e;
    return 'Unbekannter Fehler';
};
